import { isNumber } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import {
  setUrgentCareCount,
  setUrgentCareDeclineReasons,
  setUrgentCareUsers,
  setUrgentCareUsersOnDuty,
  setWaitingRoomList,
  setWaitingRoomLoading
} from 'src/store/modules/actions/urgentCare'
import { companyIsUrgentCarePopupNotificationSound$ } from 'src/store/modules/user'

import notificationSound from 'src/assets/sounds/notificationSound.mp3'
import {
  CALL_REASONS,
  END_URGENT_CARE_MEETING,
  GET_URGENT_CARE_CLINICIANS_ON_DUTY,
  GET_URGENT_CARE_CLINICIANS_SET_OFFLINE,
  GET_URGENT_CARE_COUNTER,
  GET_URGENT_CARE_USERS,
  GET_URGENT_CARE_USERS_BY_PATIENT,
  GET_WAITING_ROOM_LIST,
  PING_AS_URGENT_CARE_CLINICIAN,
  SEND_EMAIL_INVITATION,
  SEND_SMS_INVITATION,
  STOP_URGENT_CARE_RECORDING,
  UPDATE_URGENT_CARE_STATUS_ROUTES,
  UPDATE_WAITING_ROOM_ITEM,
  URGENT_CARE_DECLINE_REASONS
} from 'src/constants'
import { showInfoModal } from 'src/services/dialogs/v5'

import {
  checkIfItemPassesFilters,
  createSortFunction,
  getWaitingRoomConfig
} from './utils'

export const getWaitingRoomList = (config) => async (dispatch) => {
  dispatch(setWaitingRoomLoading(true))

  try {
    const { items, meta } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_WAITING_ROOM_LIST,
        ...config
      }
    })

    dispatch(setWaitingRoomList(items))

    return meta
  } catch (err) {
    catchError(err)
  } finally {
    dispatch(setWaitingRoomLoading(false))
  }
}

export const updateWaitingRoomItem = (uuid, payload) => async () => {
  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: UPDATE_WAITING_ROOM_ITEM,
        params: {
          uuid
        }
      },
      payload
    })
  } catch (err) {
    catchError(err)
  }
}

export const updateWaitingRoomItemDirect =
  (uuid, payload, sort, pagination, filter) => (dispatch, getState) => {
    const state = getState()

    const waitingRoomList = state.actions.urgentCare.waitingRoomList
    const isItemPresent = waitingRoomList.find((item) => item.uuid === uuid)
    const listIsFull = waitingRoomList.length === pagination.pageSize

    let updatedWaitingRoomList = [...waitingRoomList]

    const passesFilter = checkIfItemPassesFilters(payload, filter)

    if (isItemPresent) {
      if (passesFilter) {
        updatedWaitingRoomList = waitingRoomList.map((item) =>
          item.uuid === payload.uuid ? payload : item
        )
        dispatch(setWaitingRoomList(updatedWaitingRoomList))
      } else {
        updatedWaitingRoomList = waitingRoomList.filter(
          (item) => item.uuid !== payload.uuid
        )

        if (listIsFull) {
          return dispatch(
            getWaitingRoomList({
              query: getWaitingRoomConfig({ filter, sort, pagination })
            })
          )
        }

        dispatch(setWaitingRoomList(updatedWaitingRoomList))
      }
    } else {
      if (passesFilter) {
        const compareFn = createSortFunction(sort)

        updatedWaitingRoomList = [payload, ...updatedWaitingRoomList]
          .sort(compareFn)
          .slice(0, pagination.pageSize)

        dispatch(setWaitingRoomList(updatedWaitingRoomList))
      }
    }
  }

export const updateWaitingRoomItemStatus =
  (uuid, status, payload) => async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: UPDATE_URGENT_CARE_STATUS_ROUTES[status],
          params: {
            uuid
          }
        },
        payload
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchUrgentCareUsersList = () => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_URGENT_CARE_USERS
      }
    })

    dispatch(setUrgentCareUsers(items))
  } catch (err) {
    catchError(err)
  }
}

export const getUrgentCareUsersByPatientList = (uuid) => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_URGENT_CARE_USERS_BY_PATIENT,
        params: {
          uuid
        }
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getUrgentCareCounter =
  ({ shouldShowModal, text, urgentCareCount } = {}) =>
  async (dispatch, getState) => {
    try {
      let newAmount = urgentCareCount
      if (!isNumber(newAmount)) {
        const { countNew } = await httpClient.request({
          method: 'get',
          url: {
            pathname: GET_URGENT_CARE_COUNTER
          }
        })
        newAmount = countNew
      }

      if (shouldShowModal) {
        const companyIsUrgentCarePopupNotificationSound =
          companyIsUrgentCarePopupNotificationSound$(getState())
        const isPageHidden = document.hidden

        if (companyIsUrgentCarePopupNotificationSound && !isPageHidden) {
          const sound = await new Audio(notificationSound)
          const promise = sound.play()

          if (promise !== undefined) {
            promise.catch((_) => {})
          }
        }

        const modal = showInfoModal({
          content: text || 'New Patient in the Urgent Care Queue',
          title: null
        })
        setTimeout(modal.destroy, 5000)
      }

      dispatch(setUrgentCareCount(newAmount))
    } catch (err) {
      catchError(err)
    }
  }

export const pingAsUrgentCareClinician = () => async () => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: PING_AS_URGENT_CARE_CLINICIAN
      }
    })
  } catch (err) {
    catchError(err)
  }
}

export const getDeclineReasons = () => async (dispatch) => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: URGENT_CARE_DECLINE_REASONS
      }
    })

    dispatch(setUrgentCareDeclineReasons(items))
  } catch (err) {
    catchError(err)
  }
}

export const getCliniciansOnDuty =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_URGENT_CARE_CLINICIANS_ON_DUTY,
          ...config
        }
      })

      dispatch(setUrgentCareUsersOnDuty(items))
    } catch (err) {
      catchError(err)
    }
  }

export const setCliniciansOffline =
  (config = {}) =>
  async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: GET_URGENT_CARE_CLINICIANS_SET_OFFLINE,
          ...config
        }
      })
    } catch (err) {
      catchError(err)
    }
  }

export const sendSmsInvitation = (uuid, payload) => async () => {
  try {
    return await httpClient.request({
      method: 'post',
      url: {
        pathname: SEND_SMS_INVITATION,
        params: {
          uuid
        }
      },
      payload
    })
  } catch (err) {
    catchError(err)
  }
}

export const sendEmailInvitation = (uuid, payload) => async () => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: SEND_EMAIL_INVITATION,
        params: {
          uuid
        }
      },
      payload
    })
  } catch (err) {
    catchError(err)
  }
}

export const endUrgentCareCall =
  (uuid, options = {}) =>
  async () => {
    const { isRoomSwitch = false } = options

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: END_URGENT_CARE_MEETING,
          params: {
            uuid
          },
          query: isRoomSwitch ? { rtcRefresh: true } : {}
        }
      })
    } catch (err) {
      // During room switch, if meeting is already completed, log it but don't show error
      // This can happen if the meeting was already ended by another mechanism
      const isAlreadyCompleted =
        err?.response?.data?.message?.toLowerCase().includes('completed') ||
        err?.response?.data?.message?.toLowerCase().includes('complete')

      if (isRoomSwitch && isAlreadyCompleted) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Patient Switch] Meeting ${uuid} was already completed when trying to END it. ` +
            'This may indicate the meeting was ended by another mechanism. ' +
            'Error:',
          err?.response?.data?.message
        )
        return
      }

      catchError(err)
    }
  }

export const getCallReasons = async () => {
  try {
    const items = await httpClient.get({
      pathname: CALL_REASONS
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getUrgentCareUsersList = async () => {
  try {
    const items = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_URGENT_CARE_USERS
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const stopUrgentCareRecording = async (callUuid) => {
  try {
    await httpClient.patch({
      pathname: STOP_URGENT_CARE_RECORDING,
      params: { call: callUuid }
    })
  } catch (err) {
    catchError(err)
    throw err
  }
}
